<script lang="ts">
  import { browser } from '$app/environment';
  import Chevron from '$icons/Chevron.svelte';
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import { createEventDispatcher } from 'svelte';
  import DropdownBody from './DropdownBody.svelte';
  import { clickOutside } from '$utils';

  const dispatch = createEventDispatcher();

  export let label = '';
  export let description = '';
  export let name = '';
  export let errors: string[] | { _errors?: string[] } | Record<number, string[]> = [];
  export let items: string[];
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  export let icon: any = null;
  export let selectedValue: string | undefined = '';
  export let selectedValues: string[] = [];
  export let multiple = false;
  export let multipleLimit = 0;
  export let disabled = false;
  export let optional = false;
  export let required = false;
  export let placeholder = '';
  $: isDisabled = disabled || items?.length === 0;
  $: placeholderOverride =
    selectedValue || selectedValues?.length > 0 ? '' : placeholder || 'Select';

  let dropdownOpen = false;
  let searchableItems = items;
  let inputElement: HTMLInputElement;
  let filterPhrase = '';

  $: id = crypto.randomUUID();

  $: {
    handleDropdownFocus();
    resetFilterOnClose();
  }

  $: updateSearchableItems();

  function handleDropdownFocus() {
    if (browser && dropdownOpen && inputElement) {
      inputElement.focus();
    }
  }

  function resetFilterOnClose() {
    if (!dropdownOpen) {
      filterPhrase = '';
      searchableItems = items;
    }
  }

  function updateSearchableItems() {
    if (filterPhrase) {
      searchableItems = items.filter((item) =>
        item.toLowerCase().includes(filterPhrase.toLowerCase()),
      );
    } else {
      searchableItems = items;
    }
  }

  function hasErrors(
    errors: string[] | { _errors?: string[] } | Record<number, string[]>,
  ): boolean {
    if (typeof errors === 'object' && '_errors' in errors) {
      return Array.isArray(errors._errors) && errors._errors.length > 0;
    }

    if (Array.isArray(errors)) {
      return errors.filter((error) => typeof error === 'string').length > 0;
    }

    return false;
  }
</script>

<div
  class="font-inter relative flex w-full flex-col items-start text-xs font-normal leading-4 {$$props.class}"
  use:clickOutside={() => {
    dropdownOpen = false;
  }}
>
  <label for={id} class="">
    {#if label}
      <p class="font-architekt text-gray-dark-12 mb-2 text-sm font-normal leading-5">
        {label}
        {#if optional}
          <span class="text-gray-dark-10 text-[12px]">(Optional)</span>
        {/if}
        {#if required}
          <span class="text-red-dark-10 text-[12px]">*</span>
        {/if}
      </p>
    {/if}
    {#if description}
      <p class="text-gray-dark-11 mb-2 mt-1 text-xs">
        {description}
      </p>
    {/if}
  </label>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    on:click={() => !isDisabled && (dropdownOpen = true)}
    class="input-container {hasErrors(errors) ? 'border-red-dark-10' : 'border-gray-dark-4'}"
    class:focus={dropdownOpen}
    class:disabled={isDisabled}
  >
    <div class="flex items-center">
      <span class="w-0 whitespace-nowrap">
        {#if selectedValue || selectedValues?.length > 0}
          {#if selectedValues?.length >= 3}
            {selectedValues[0]} and {selectedValues?.length - 1} more
          {:else if selectedValues?.length > 0 && selectedValues?.length < 3}
            {selectedValues?.join(', ')}
          {:else if selectedValue}
            {selectedValue}
          {/if}
        {:else}
          <span class="text-gray-dark-8">{placeholderOverride}</span>
        {/if}
      </span>
      {#if dropdownOpen}
        <input
          bind:this={inputElement}
          bind:value={filterPhrase}
          on:keydown={(e) => {
            if (e.key == 'Backspace' && filterPhrase?.length == 0) {
              selectedValues = [];
              selectedValue = '';
              // Escape key doesn't work properly on chrome for some reason :(
            } else if (e.key == 'Enter' || e.key == 'Escape' || e.key == 'Esc') {
              dropdownOpen = false;
            }
          }}
          type="text"
          class="ml-2 flex-1 bg-transparent outline-none"
        />
      {/if}
    </div>
    <button
      type="button"
      disabled={isDisabled}
      on:click|stopPropagation|preventDefault={() => !isDisabled && (dropdownOpen = !dropdownOpen)}
      tabindex={-1}
    >
      <Chevron
        class="text-gray-dark-9 float-right transform duration-500 {dropdownOpen
          ? '-rotate-180'
          : 'rotate-0'}"
      />
    </button>
  </div>

  {#if dropdownOpen && searchableItems?.length > 0}
    <div class="relative w-full">
      <div class="absolute inset-x-0">
        <DropdownBody
          bind:dropdownOpen
          bind:items={searchableItems}
          bind:selectedValue
          bind:selectedValues
          bind:multiple
          bind:multipleLimit
          bind:icon
          on:change={() => {
            filterPhrase = '';
            dispatch('change', multiple ? selectedValues : selectedValue);
          }}
        />
      </div>
    </div>
  {/if}
  {#if hasErrors(errors)}
    {#if '_errors' in errors && Array.isArray(errors._errors)}
      {#each errors._errors as errorMessage}
        <div class="text-red-dark-10 mt-2 flex items-center justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon />
          <p class="text-red-dark-10">{errorMessage}</p>
        </div>
      {/each}
    {:else if Array.isArray(errors)}
      {#each errors as errorMessage}
        <div class="text-red-dark-10 mt-2 flex items-center justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon />
          <p class="text-red-dark-10">{errorMessage}</p>
        </div>
      {/each}
    {/if}
  {/if}
</div>
<!-- 
	Hack to force name attribute.
	This is handy for forms that use form actions
 -->
{#if multiple}
  {#if selectedValues?.length > 0}
    {#each selectedValues as value}
      <input class="sr-only" tabindex={-1} type="text" {name} {value} />
    {/each}
  {/if}
{:else}
  <input class="sr-only" tabindex={-1} type="text" {name} bind:value={selectedValue} />
{/if}

<style lang="postcss">
  .input-container {
    @apply gap-x-lg px-lg text-gray-dark-12 caret-mint-dark-11 relative flex h-10 w-full cursor-pointer items-center justify-between rounded border bg-transparent outline-none ring-[3px] ring-transparent;
  }
  .input-container:disabled {
    @apply bg-gray-dark-3 cursor-not-allowed;
  }

  .focus {
    @apply border-mint-dark-8 ring-mint-dark-4;
  }
  .input-container:hover {
    @apply border-gray-dark-5;
  }

  .input-container.disabled {
    @apply bg-gray-dark-3 cursor-not-allowed opacity-50;
  }
</style>
